import { isSameWalletAddress } from "../blockchain/wallet-address"

/**
 * Whether a sell order can still be bought, shared by the BFF (which hides
 * dead listings) and the market UI.
 *
 * Union can keep an order ACTIVE after it stopped being fillable. An order
 * indexer saves an expired order as ended and then publishes the change; a
 * publish lost to a Kafka outage is never retried. On 2026-10-14 Union held
 * ~63k such orders on Base, Polygon and Robinhood, priced the items with them
 * and named their sellers as owners. So a listing is checked, not trusted.
 */

/**
 * The fields of a sell order the check reads, all optional: a Union `Order`
 * or a marketplace search `OrderShort` both fit.
 */
export interface ListingLivenessOrder {
  maker?: string | null
  /** Union `Order.endedAt`, ISO. */
  endedAt?: string | null
  /** Marketplace `OrderShort.expirationDate`: typed `Date`, ISO at runtime. */
  expirationDate?: string | Date | null
}

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/

/** The bare address of a union `BLOCKCHAIN:address` id, or of a bare one. */
function rawAddress(address: string): string {
  return address.slice(address.indexOf(":") + 1)
}

/**
 * When the listing stops being fillable, in ms, or undefined when it never
 * does. A missing or unreadable date, or the epoch, means no end: Rarible V2
 * orders without an end (`end = 0`) reach us as 1970-01-01.
 */
export function getListingEndTime(
  order: ListingLivenessOrder,
): number | undefined {
  const end = order.endedAt || order.expirationDate
  if (!end) return undefined
  const time = new Date(end).getTime()
  return Number.isFinite(time) && time > 0 ? time : undefined
}

/** Whether the listing has ended; Seaport and Rarible V2 both end at `end`. */
export function isListingExpired(
  order: ListingLivenessOrder | null | undefined,
  { now = Date.now() }: { now?: number } = {},
): boolean {
  if (!order) return false
  const end = getListingEndTime(order)
  return end !== undefined && end <= now
}

/**
 * Whether the listing's maker is an EVM wallet. EVM marketplaces leave a
 * listed NFT in its seller's wallet; Solana ones escrow it.
 */
export function isEvmListing(
  order: ListingLivenessOrder | null | undefined,
): boolean {
  return !!order?.maker && EVM_ADDRESS.test(rawAddress(order.maker))
}

/**
 * Whether the listing can no longer be bought: it has ended, or, on EVM, the
 * item's single owner is not its maker, so the NFT was sold or moved and the
 * order can only revert. `owner` is the indexer's single owner; without one
 * (several holders, or unknown) only the end is checked. Solana owners are
 * not compared: a listed NFT sits in a marketplace escrow there.
 */
export function isDeadListing(
  order: ListingLivenessOrder | null | undefined,
  { owner, now = Date.now() }: { owner?: string | null; now?: number } = {},
): boolean {
  if (!order) return false
  if (isListingExpired(order, { now })) return true
  if (!owner || !order.maker) return false
  const ownerAddress = rawAddress(owner)
  const makerAddress = rawAddress(order.maker)
  return (
    EVM_ADDRESS.test(ownerAddress) &&
    EVM_ADDRESS.test(makerAddress) &&
    !isSameWalletAddress(ownerAddress, makerAddress)
  )
}
